import type {State} from '../../../../../reducers/types'
import {getLocator} from '../../../../../rest/locators'
import {getStatusKey} from '../../../../../rest/schemata'
import {
  getBuildsData,
  getBuildsLocatorWithPage,
  getBuildType,
  getShowQueuedBuildsInBuildsList,
  getAutoExpandQueuedBuilds,
  getStatusByKey,
  getIsSakuraUI,
} from '../../../../../selectors'
import {
  getBuildTypeStatusRequest,
  getProjectBuildTypeFilter,
  toBuildTypeId,
} from '../../../../../types'
import {getPager} from '../../../../common/Pager/Pager.selectors'
import {PagerGroup} from '../../../../common/Pager/Pager.types'

import type {OwnProps, StateProps} from './BuildTypeHistory.types'

const isAllState = (buildState: readonly string[] | undefined): boolean =>
  buildState == null || buildState.length === 0

const statesInclude = (buildState: readonly string[] | undefined, candidates: string[]): boolean =>
  buildState?.some(s => candidates.includes(s)) ?? false

export const mapStateToBuildTypeHistoryProps = (
  state: State,
  {
    buildTypeId = toBuildTypeId(''),
    branch,
    withCollapsedQueued,
    withRunningAndQueued,
    buildState = [],
    pinned,
    starred,
    tag,
    agentPattern,
  }: Partial<OwnProps>,
): StateProps => {
  const shouldLoadRunningAndQueued =
    withRunningAndQueued === true &&
    (isAllState(buildState) || statesInclude(buildState, ['running', 'queued']))
  const shouldShowCollapsibleQueuedBuilds =
    withCollapsedQueued === true && isAllState(buildState) && !pinned && !starred
  const statusKey = getStatusKey(getBuildTypeStatusRequest(buildTypeId, branch))
  const status = getStatusByKey(state, statusKey)
  const hasQueued = Boolean(status?.queued)
  const commonLocatorOptions = {
    projectBuildtype: getProjectBuildTypeFilter({
      buildTypeId,
    }),
    withRunningAndQueued: shouldLoadRunningAndQueued,
    branch,
    buildState,
    pinned,
    starred,
    tag,
    agentPattern,
  }
  const allBuildsLocator = getLocator(commonLocatorOptions)
  const startedBuildsLocator = getLocator({
    ...commonLocatorOptions,
    runningAndFinished: true,
  })
  const {inited, loading, data, ready} = getBuildsData(
    state,
    getBuildsLocatorWithPage(state, startedBuildsLocator, 1),
  )
  const noStartedBuilds = inited && (!loading || ready) && data.length === 0
  const hasQueuedOnly = hasQueued && noStartedBuilds
  const showQueuedBuilds = shouldShowCollapsibleQueuedBuilds
    ? getShowQueuedBuildsInBuildsList(state) ||
      getAutoExpandQueuedBuilds(state, statusKey) ||
      hasQueuedOnly
    : true
  const locator = showQueuedBuilds ? allBuildsLocator : startedBuildsLocator
  // While the queue is collapsed, queued builds aren't fetched, hiding ones waiting for approval.
  // Locator for the lightweight probe that finds approval-blocked builds among the collapsed queue.
  // It reuses commonLocatorOptions (so any tag/agent filter applies); count is pinned to the queued
  // total so every queued build is returned, and lookupLimit matches the main build list's scan
  // window so the probe is bounded exactly like allBuildsLocator rather than a smaller default.
  // The probe runs even with a tag filter active (when the toggler is otherwise hidden) so a
  // tag-matching approval build still gets merged into the list.
  const queuedCount = status?.queued ?? 0
  const buildsLookupLimit = getPager(state, PagerGroup.BUILD).lookupLimit
  const approvalProbeLocator =
    shouldShowCollapsibleQueuedBuilds && !showQueuedBuilds && hasQueued
      ? `${getLocator({
          ...commonLocatorOptions,
          withRunningAndQueued: false,
          buildState: ['queued'],
        })},count:${queuedCount},lookupLimit:${buildsLookupLimit}`
      : undefined
  const buildType = getBuildType(state, buildTypeId)
  return {
    withCollapsedQueued: shouldShowCollapsibleQueuedBuilds,
    shouldLoadRunningAndQueued,
    showQueuedBuilds,
    hasQueuedOnly,
    locator,
    approvalProbeLocator,
    internalId: buildType?.internalId,
    isSakuraUI: getIsSakuraUI(state),
    composite: buildType?.type === 'composite',
  }
}
